import { firstLoadPending } from '@/api/firstLoadPending';
import { useEffect, useRef } from 'react';
import { Text } from '@/components/primitives';
import { Icons, vars } from '@/theme';
import { StyledDialog } from '@/components/dialogs';
import { testIds } from '@/test/testIds';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOrganizations, useNetworks } from '@/features/context/hooks/useContextSwitching';
import { ContextSwitcherList } from '../ContextSwitcherList';
import { reportClientError } from '@/api/clientErrors';

export interface ForcedContextSelectionModalProps {
  open: boolean;
}

/**
 * ForcedContextSelectionModal — non-dismissible gate shown when a user has
 * `canSwitchContext=true` but no context selected (`context.type=null`). The
 * user MUST pick an org/network before doing anything else; there is no close
 * button and no backdrop/escape dismiss.
 *
 * This is a thin wrapper around the SAME searchable {@link ContextSwitcherList}
 * used by the user-menu Organization/Network dialog — so the forced flow gets
 * the real search box, styling, and (for staff) the create control for free.
 * The differences from the user-menu dialog are only:
 *   - the "current context" identity block is replaced by a plain
 *     "Select Organization/Network" header (there IS no current context yet), and
 *   - the switcher is always expanded (no collapsible disclosure).
 *
 * Selection is one-click: {@link ContextSwitcherList} switches immediately and
 * calls `updateCurrentUser`, which flips `requiresContextSelection` to false and
 * auto-closes this modal (AppShell stops rendering it). No Continue button.
 *
 * Single-association users never reach here: the backend hands them their sole
 * context already set on `/auth/me`, so `requiresContextSelection` is false.
 *
 * Zero-association handling — a user forced to select with NO orgs/networks:
 *   - Non-staff: an anomaly (the backend should not force selection on them).
 *     Raise a critical alert and let them proceed as though canSwitchContext
 *     were false (render nothing) rather than trapping them behind a dead-end
 *     "contact your administrator" screen with no escape.
 *   - Staff: NOT a dead end — they can search/create ANY context regardless of
 *     membership, so keep them in the workflow (the empty default list is fine).
 */
export function ForcedContextSelectionModal({ open }: ForcedContextSelectionModalProps) {
  const { isStaff } = useCurrentUser();

  // Fetch to (a) label the header by which context TYPES exist and (b) detect
  // the zero-association anomaly. Shares the query key/cache with
  // ContextSwitcherList's own unfiltered fetch, so this costs no extra request.
  const {
    data: organizations = [], isPending: orgsLoadingPending, fetchStatus: orgsLoadingFetchStatus, isError: orgsError,
  } = useOrganizations({ enabled: open });
  // Not TanStack's `isLoading`: a first load paused offline is still waiting (#1569, #1561-g).
  const orgsLoading = firstLoadPending({ isPending: orgsLoadingPending, fetchStatus: orgsLoadingFetchStatus });
  const {
    data: networks = [], isPending: networksLoadingPending, fetchStatus: networksLoadingFetchStatus, isError: networksError,
  } = useNetworks({ enabled: open });
  // Not TanStack's `isLoading`: a first load paused offline is still waiting (#1569, #1561-g).
  const networksLoading = firstLoadPending({ isPending: networksLoadingPending, fetchStatus: networksLoadingFetchStatus });

  const listsSettled = !orgsLoading && !networksLoading;
  const hasOrgs = Array.isArray(organizations) && organizations.length > 0;
  const hasNetworks = Array.isArray(networks) && networks.length > 0;
  // Only treat as "no associations" once the lists have actually loaded without
  // error — a load error is not proof of zero memberships.
  const hasNoAssociations =
    listsSettled && !orgsError && !networksError && !hasOrgs && !hasNetworks;

  // Non-staff, forced to select, but belongs to nothing: skip the workflow and
  // alert. Staff stay in (they can reach any context via search/create).
  const shouldSkipForNoAssociations = open && !isStaff && hasNoAssociations;

  // Fire the critical alert exactly once per mount, not on every render.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (!shouldSkipForNoAssociations || reportedRef.current) return;
    reportedRef.current = true;
    reportClientError({
      error: new Error(
        'Forced context selection with zero org/network associations (non-staff)',
      ),
      source: 'context',
      context: { reason: 'no_associations', isStaff: false },
    });
  }, [shouldSkipForNoAssociations]);

  if (shouldSkipForNoAssociations) return null;

  // Header label mirrors the user-menu dialog's switch label: staff (or anyone
  // with both kinds) get the combined label; otherwise narrow to the one kind
  // they can pick. Keep the combined label until the lists settle to avoid a
  // flash of the wrong (narrower) label during load.
  const headerLabel =
    isStaff || !listsSettled || (hasOrgs && hasNetworks)
      ? 'Select Organization/Network'
      : hasNetworks
        ? 'Select Network'
        : 'Select Organization';

  return (
    <StyledDialog
      open={open}
      title={headerLabel}
      hideTitle
      size="standard"
      fitBody
      hideClose
      closeDisabled
      footer={null}
      DialogProps={{
        'data-testid': testIds.forcedSelection.modal,
      }}
    >
      {/* Header — replaces the user-menu dialog's "current context" identity
          block: there is no current context to show, so just say what to do. */}
      <div style={{ marginBottom: 20, textAlign: 'center', flexShrink: 0 }}>
        <Icons.switchContext
          style={{ fontSize: 48, color: vars.color.success.base, marginBottom: 8 }}
        />
        <Text
          variant="pageHeader"
          data-testid={testIds.forcedSelection.title}
          style={{ fontWeight: 600, display: 'block' }}
        >
          {headerLabel}
        </Text>
        <Text
          color="secondary"
          data-testid={testIds.forcedSelection.description}
          style={{ marginTop: 8 }}
        >
          Choose where you want to work to continue.
        </Text>
      </div>

      {/* The standard searchable switcher, always expanded (no disclosure).
          Switching is one-click and auto-closes this modal. */}
      <ContextSwitcherList />
    </StyledDialog>
  );
}
